import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, ShieldCheck, Wrench, Clock, Snowflake, Flame, Wind, Video, BadgeDollarSign } from "lucide-react";
import { BUSINESS, SERVICES, HERO_IMAGE } from "@/lib/business";
import FAQ from "@/components/FAQ";

const TRUST = [
  { icon: Video, label: "Free phone & video consult" },
  { icon: BadgeDollarSign, label: "$65 service call" },
  { icon: ShieldCheck, label: "Licensed & insured" },
];

const TICKER = [
  "Walk-in coolers",
  "Reach-in freezers",
  "Ice machines",
  "Rooftop units",
  "Mini-splits",
  "Convection ovens",
  "Combi ovens",
  "Fryers",
  "Char-broilers",
  "Display cases",
  "Prep tables",
  "Beverage coolers",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Technician servicing equipment"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="relative grain-overlay">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 lg:pt-28 lg:pb-40">
            <div className="reveal inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-cyan-300">
              <MapPin className="w-3.5 h-3.5" />
              Serving {BUSINESS.serviceArea}
            </div>
            <h1
              data-testid="hero-title"
              className="reveal reveal-2 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl mt-6 max-w-4xl leading-[0.95]"
            >
              Repairs you can trust.<br />
              <span className="text-cyan-400">Cold air. Hot grills.</span> Always running.
            </h1>
            <p className="reveal reveal-3 mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              {BUSINESS.name} keeps Southern Oregon kitchens, cafés, markets and homes running.
              HVAC, commercial refrigeration, and cooking equipment — diagnosed, fixed, and
              back online fast.
            </p>

            <div className="reveal reveal-4 mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-request"
                className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-7 py-4 font-semibold tracking-wide text-sm uppercase transition-colors"
              >
                Request Service <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-cta-call"
                className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-7 py-4 font-semibold tracking-wide text-sm uppercase transition-colors"
              >
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
            </div>

            <div className="reveal reveal-4 mt-12 grid grid-cols-3 gap-6 max-w-2xl">
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-3 border-l border-white/15 pl-4">
                  <t.icon className="w-5 h-5 text-cyan-400 shrink-0" />
                  <div className="text-xs sm:text-sm text-slate-300 font-medium">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <section className="bg-blue-900 text-white border-y border-blue-800 overflow-hidden">
        <div className="flex whitespace-nowrap py-4 ticker-track">
          {[...TICKER, ...TICKER].map((item, idx) => (
            <div key={idx} className="flex items-center px-8 text-sm font-semibold tracking-[0.18em] uppercase text-cyan-300">
              <span className="text-white">{item}</span>
              <span className="mx-8 text-cyan-500">/</span>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING / CONSULT STRIP */}
      <section data-testid="pricing-strip" className="bg-cyan-500 text-slate-950 border-b border-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5 flex items-start gap-5">
            <Video className="w-10 h-10 shrink-0 mt-1" />
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold">Free</div>
              <div className="font-display font-black text-2xl sm:text-3xl leading-tight mt-1">
                Phone &amp; video consultation
              </div>
              <p className="text-sm text-slate-900/80 mt-2 leading-relaxed">
                Talk through what's wrong before any truck rolls. No obligation, no charge.
              </p>
            </div>
          </div>
          <div className="md:col-span-4 flex items-start gap-5 md:border-l md:border-slate-950/15 md:pl-8">
            <BadgeDollarSign className="w-10 h-10 shrink-0 mt-1" />
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold">Base price</div>
              <div className="font-display font-black text-2xl sm:text-3xl leading-tight mt-1">
                $65 service call
              </div>
              <p className="text-sm text-slate-900/80 mt-2 leading-relaxed">
                Onsite diagnostic visit. Repairs quoted in writing before any work begins.
              </p>
            </div>
          </div>
          <div className="md:col-span-3 flex flex-col gap-2 md:items-end">
            <a
              href={BUSINESS.phoneHref}
              data-testid="pricing-strip-call"
              className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-6 py-3.5 text-sm font-semibold tracking-wide uppercase transition-colors"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <Link
              to="/contact"
              data-testid="pricing-strip-form"
              className="inline-flex items-center gap-2 border border-slate-950 hover:bg-slate-950 hover:text-cyan-400 text-slate-950 px-6 py-3.5 text-sm font-semibold tracking-wide uppercase transition-colors"
            >
              Book consult <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="text-xs tracking-[0.22em] uppercase font-semibold text-blue-900">
                What we fix
              </div>
              <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-4">
                Three trades. <br />One technician.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-base text-slate-600 leading-relaxed">
                From a walk-in cooler at 3 a.m. to a fryer that won't ignite before lunch
                rush — we work on the systems your business depends on. Diagnostics first,
                straight talk second, and repair you'll only pay for once.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((s, idx) => (
              <Link
                to="/services"
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="group bg-white border border-slate-200 hover:border-blue-900 transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-200">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-blue-900">
                      {s.label}
                    </div>
                    <div className="text-blue-900 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900 mt-3">
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.short}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.22em] uppercase font-semibold text-cyan-400">
              How it works
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-4">
              Tell us what's broken. <br />We'll tell you when we'll be there.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { n: "01", t: "You contact us", d: "Phone, email, or the request form — share the equipment & symptoms." },
                { n: "02", t: "We diagnose", d: "Onsite assessment with a clear, written estimate before any work." },
                { n: "03", t: "We repair", d: "Fixed right the first time, with parts and labor stood behind." },
              ].map((step) => (
                <div key={step.n} className="border-l border-cyan-500/40 pl-5">
                  <div className="font-display font-black text-3xl text-cyan-400">{step.n}</div>
                  <div className="font-semibold text-white mt-3">{step.t}</div>
                  <div className="text-sm text-slate-400 mt-1 leading-relaxed">{step.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 md:col-start-9 bg-cyan-500 text-slate-950 p-10">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase font-bold">
              <Snowflake className="w-4 h-4" /> Down equipment?
            </div>
            <h3 className="font-display font-black text-3xl mt-3 leading-tight">
              Skip the form. Call now.
            </h3>
            <a
              href={BUSINESS.phoneHref}
              data-testid="cta-call-now"
              className="mt-6 inline-flex items-center gap-2 bg-slate-950 text-cyan-400 hover:bg-slate-800 px-6 py-4 text-sm font-semibold tracking-wide uppercase transition-colors w-full justify-center"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <Link
              to="/contact"
              data-testid="cta-form-link"
              className="mt-3 inline-flex items-center gap-2 border border-slate-950/30 hover:bg-slate-950/10 text-slate-950 px-6 py-4 text-sm font-semibold tracking-wide uppercase transition-colors w-full justify-center"
            >
              Use the request form <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                Icon: Wind,
                t: "HVAC expertise",
                d: "Residential and light commercial systems — diagnosis, repair, and seasonal tune-ups.",
              },
              {
                Icon: Snowflake,
                t: "Refrigeration first",
                d: "Specialists in commercial cold-side equipment — protect your inventory and uptime.",
              },
              {
                Icon: Flame,
                t: "Hot-side too",
                d: "We service the cook line so your kitchen never goes dark in the middle of service.",
              },
            ].map((b) => (
              <div key={b.t} className="border-t border-slate-300 pt-8">
                <b.Icon className="w-7 h-7 text-blue-900" />
                <h3 className="font-display font-bold text-xl text-slate-900 mt-5">{b.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />
    </div>
  );
}
